import { Outlet, Link, useLocation, useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useUserSystems } from "@/hooks/useUserSystems";
import { useExpenseBadges } from "@/hooks/use-expense-badges";
import { cn } from "@/lib/utils";
import { SYSTEM_CONFIGS, getSystemConfig, getModuleIcon } from "@/lib/system-icons";
import {
  Building2,
  BarChart3,
  User,
  LogOut,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Package,
  Lock,
  Sun,
  Moon,
  Monitor,
  Settings,
  LayoutDashboard,
  Car,
  UserCircle,
  Wrench,
  Building,
  DollarSign,
  Cog,
  TrendingUp,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarFooter,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AdminSection } from "@/components/company/AdminSection";
import { useTheme } from "@/contexts/theme-provider";
import { useEffect, useMemo, useState } from "react";

function CompanyUserSidebar({ 
  user, 
  profile, 
  signOut, 
  setTheme 
}: { 
  user: any; 
  profile: any; 
  signOut: () => void; 
  setTheme: (theme: string) => void; 
}) {
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const { companies } = useAuth();
  const { data: userSystems = [] } = useUserSystems();
  console.log('🔍 [CompanyUserLayout] User systems:', userSystems);

  const company = companies.find(c => c.slug === slug);
  const currentPath = location.pathname;

  const [expandedSystems, setExpandedSystems] = useState<Record<string, boolean>>({});
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

  const expenseBadge = useExpenseBadges();

  // Função isActive mais robusta que considera query/hash
  const isActive = (path: string) => {
    const cleanPath = path.endsWith("/") ? path.slice(0, -1) : path;
    const cleanCurrent = currentPath.endsWith("/") ? currentPath.slice(0, -1) : currentPath;
    return cleanCurrent === cleanPath || cleanCurrent.startsWith(cleanPath + "/");
  };

  const isSystemActive = (systemCode: string) =>
    currentPath.includes(`/systems/${systemCode}`);

  const toggleSystem = (systemCode: string) => {
    setExpandedSystems(prev => ({ ...prev, [systemCode]: !prev[systemCode] }));
  };

  const renderModuleBadge = (moduleCode: string) => {
    if (moduleCode === 'expenses' && expenseBadge) {
      return (
        <Badge 
          variant={expenseBadge.variant} 
          className="ml-auto text-[10px] px-1.5 py-0.5 h-4 min-w-[20px] flex items-center justify-center"
        >
          {expenseBadge.count}
        </Badge>
      );
    }
    return null;
  };

  // Auto-expand do sistema ativo
  useEffect(() => {
    userSystems.forEach(system => {
      if (isSystemActive(system.code)) {
        setExpandedSystems(prev => ({ ...prev, [system.code]: true }));
        
        // Auto-expand módulos com submódulos ativos
        const systemConfig = getSystemConfig(system.code);
        systemConfig?.modules.forEach(module => {
          if (module.subModules) {
            const hasActiveSubModule = module.subModules.some(sub => 
              currentPath.includes(sub.path)
            );
            if (hasActiveSubModule) {
              setExpandedModules(prev => ({ 
                ...prev, 
                [`${system.code}-${module.code}`]: true 
              }));
            }
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  // Base navigation items
  const baseNavItems = useMemo(() => ([
    { title: "Área do Usuário", url: `/company/${slug}/user`, icon: BarChart3 },
  ]), [slug]);

  // Mapeia sistemas com seus módulos e ícones
  const systemsWithModules = useMemo(() => {
    return userSystems.map((system: any) => {
      const config = getSystemConfig(system.code);
      return {
        ...system,
        icon: config?.icon ?? Package,
        modules: config?.modules || [],
      };
    });
  }, [userSystems]);

  return (
    <Sidebar className="border-r bg-background">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="flex items-center gap-2 px-4 py-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-primary">
              <Building2 className="h-3 w-3 text-primary-foreground" />
            </div>
            {company?.name}
            <Badge variant="outline" className="ml-auto text-xs">
              Usuário
            </Badge>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {baseNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {systemsWithModules.length > 0 ? (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {systemsWithModules.map((system: any) => {
                  const SystemIcon = system.icon || Package;
                  const isExpanded = Boolean(expandedSystems[system.code]);
                  const systemActive = isSystemActive(system.code);

                  const hasModules = Array.isArray(system.modules) && system.modules.length > 0;

                  return (
                    <SidebarMenuItem key={system.id}>
                      <SidebarMenuButton
                        className={cn(
                          "w-full justify-between",
                          systemActive && "bg-accent text-accent-foreground"
                        )}
                        onClick={() => toggleSystem(system.code)}
                        aria-expanded={isExpanded}
                        aria-controls={`system-${system.code}-submenu`}
                      >
                        <div className="flex items-center gap-2">
                          <SystemIcon className="h-4 w-4" />
                          <span>{system.name}</span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </SidebarMenuButton>

                      {isExpanded && (
                        <SidebarMenuSub id={`system-${system.code}-submenu`}>
                        {hasModules ? (
                            system.modules.map((module: any) => {
                              const moduleUrl = `/company/${slug}/user/systems/${system.code}${module.path}`;
                              const ModuleIcon = module.icon || getModuleIcon(module.code);
                              const hasSubModules = Array.isArray(module.subModules) && module.subModules.length > 0;
                              const moduleKey = `${system.code}-${module.code}`;
                              const isModuleExpanded = Boolean(expandedModules[moduleKey]);

                              // Se o módulo tem submódulos, renderiza como Collapsible
                              if (hasSubModules) {
                                return (
                                  <Collapsible 
                                    key={moduleKey}
                                    open={isModuleExpanded}
                                    onOpenChange={(open) => setExpandedModules(prev => ({ ...prev, [moduleKey]: open }))}
                                  >
                                    <SidebarMenuSubItem>
                                      <CollapsibleTrigger asChild>
                                        <SidebarMenuSubButton className="w-full justify-between">
                                          <div className="flex items-center gap-2 flex-1">
                                            <ModuleIcon className="h-4 w-4" />
                                            <span className="flex-1">{module.name}</span>
                                            {renderModuleBadge(module.code)}
                                          </div>
                                          {isModuleExpanded ? (
                                            <ChevronDown className="h-3 w-3" />
                                          ) : (
                                            <ChevronRight className="h-3 w-3" />
                                          )}
                                        </SidebarMenuSubButton>
                                      </CollapsibleTrigger>
                                      <CollapsibleContent>
                                         <SidebarMenuSub>
                                          {module.subModules.map((subModule: any) => {
                                            const subModuleUrl = `/company/${slug}/user/systems/${system.code}${subModule.path}`;
                                            const SubModuleIcon = subModule.icon || getModuleIcon(subModule.code);
                                            
                                            return (
                                              <SidebarMenuSubItem key={`${moduleKey}-${subModule.code}`}>
                                                <SidebarMenuSubButton asChild isActive={isActive(subModuleUrl)}>
                                                  <Link to={subModuleUrl} className="flex items-center gap-2 pl-4">
                                                    <SubModuleIcon className="h-3.5 w-3.5" />
                                                    <span>{subModule.name}</span>
                                                  </Link>
                                                </SidebarMenuSubButton>
                                              </SidebarMenuSubItem>
                                            );
                                          })}
                                        </SidebarMenuSub>
                                      </CollapsibleContent>
                                    </SidebarMenuSubItem>
                                  </Collapsible>
                                );
                              }

                              // Módulos sem submódulos
                              return (
                                <SidebarMenuSubItem key={moduleKey}>
                                  <SidebarMenuSubButton asChild isActive={isActive(moduleUrl)}>
                                    <Link to={moduleUrl} className="flex items-center gap-2 w-full">
                                      <ModuleIcon className="h-4 w-4" />
                                      <span className="flex-1">{module.name}</span>
                                      {renderModuleBadge(module.code)}
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })
                          ) : (
                            <SidebarMenuSubItem>
                              <span className="text-muted-foreground text-sm">Sem módulos</span>
                            </SidebarMenuSubItem>
                          )}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <SidebarGroup>
            <SidebarGroupContent>
              <div className="px-4 py-3 text-sm text-muted-foreground">
                Nenhum sistema habilitado para este usuário.
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Admin Section */}
        <AdminSection />
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to={`/company/${slug}/user/admin/support`}>
                <HelpCircle className="h-4 w-4" />
                <span>Suporte</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          
          {/* Menu do Usuário */}
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="w-full">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback>
                      {profile?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{profile?.full_name || user?.email || 'Usuário'}</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to={`/company/${slug}/user/profile`}>
                    <User className="mr-2 h-4 w-4" />
                    Perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTheme("light")}>
                  <Sun className="mr-2 h-4 w-4" />
                  Tema Claro
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>
                  <Moon className="mr-2 h-4 w-4" />
                  Tema Escuro
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>
                  <Monitor className="mr-2 h-4 w-4" />
                  Sistema
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

export default function CompanyUserLayout() {
  const { slug } = useParams<{ slug: string }>();
  const { companies, user, loading, signOut, profile, setActiveCompany } = useAuth();
  const { setTheme } = useTheme();
  const location = useLocation();
  const isLiveMap = location.pathname.includes('/systems/fleet/monitoring/live');

  const company = companies.find(c => c.slug === slug);

  // Sync active company with URL slug
  useEffect(() => {
    console.log('[CompanyUserLayout] Setting active company:', { 
      companyId: company?.id, 
      companySlug: company?.slug 
    });
    if (company && company.id) {
      setActiveCompany(company);
    }
  }, [company, setActiveCompany]);

  if (loading) {
    return <div className="flex h-screen items-center justify-center">Carregando...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!company) return <Navigate to="/" replace />;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        {/* Sidebar precisa ser filho direto do Provider */}
        <CompanyUserSidebar 
          user={user}
          profile={profile}
          signOut={signOut}
          setTheme={setTheme}
        />

        {/* Conteúdo principal também dentro do Provider */}
        <div className="flex-1 flex flex-col">
          {!isLiveMap && (
            <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="flex h-14 items-center gap-4 px-6">
                <SidebarTrigger />
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  <h1 className="font-semibold">{company.name}</h1>
                </div>
              </div>
            </header>
          )}

          <main className={isLiveMap ? "p-0" : "p-6"}>
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}